import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PerkTags from '../../components/PerkTag';
import TierCrest from '../../components/stamps/TierCrest';
import { LocalLink, useLang } from '../../i18n/useLocalizedNav';
import { TIERS } from '../../utils/tiers';
import { getLocations } from '../../content/catalog';
import Reveal from '../../components/Reveal';

const Tiers: React.FC = () => {
  const lang = useLang();
  const { t } = useTranslation();

  // Discounts come from the static localized catalog (name/region live
  // in the prose layer — the API only returns structural fields). We
  // take the first 3 active discounts so the order is deterministic;
  // any randomness here would cause a hydration mismatch (prerender
  // captures one order, client renders another → React #418).
  const discounts = useMemo(
    () =>
      getLocations(lang)
        .filter((l) => (l.discountPercent ?? 0) > 0)
        .slice(0, 3),
    [lang],
  );

  return (
  <section className="space-y-10 py-8">
    <div className="space-y-4">
      <p className="font-heading font-bold text-sm tracking-[0.25em] text-brand-accent uppercase">
        {t('home_tiers_eyebrow')}
      </p>
      <h2 className="font-display text-warm-800 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
        {t('home_tiers_title_pre')}{' '}
        <span className="italic text-brand-accent">
          {t('home_tiers_title_accent')}
        </span>
      </h2>
      <p className="text-warm-500 text-lg leading-relaxed max-w-2xl">
        {t('home_tiers_lead')}
      </p>
    </div>

    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
      {TIERS.map((tier, i) => (
        <Reveal key={tier.short} delay={i * 60} className="h-full">
          <div
            className={`relative rounded-2xl p-6 flex flex-col items-center text-center space-y-4 border h-full ${
              tier.top
                ? 'bg-brand-secondary border-brand-secondary'
                : 'bg-white border-warm-200/60'
            }`}
          >
            {tier.top && (
              <span className="absolute -top-3 right-4 bg-brand-primary text-brand-secondary text-[10px] font-heading font-bold px-2 py-1 rounded-md tracking-wider">
                {t('home_top_tier')}
              </span>
            )}
            <TierCrest color={tier.crest} label={tier.short} />
            <div className="space-y-1">
              <h3
                className={`font-display text-xl ${
                  tier.top ? 'text-brand-primary' : 'text-warm-800'
                }`}
              >
                {t(`tier_${tier.short}`)}
              </h3>
              <p
                className={`font-heading font-bold text-[10px] tracking-[0.15em] uppercase ${
                  tier.top ? 'text-warm-400' : 'text-warm-500'
                }`}
              >
                {t('tier_range_stamps', { range: tier.stampRange })}
              </p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>

    {/* Active now */}
    <div className="bg-brand-secondary rounded-3xl p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <div className="space-y-4">
        <p className="font-heading font-bold text-sm tracking-[0.25em] text-brand-accent-light uppercase">
          {t('home_tiers_active_now')}
        </p>
        <h3 className="font-display text-3xl md:text-4xl text-white leading-tight">
          <span className="text-brand-primary">{t('home_tiers_month_1')}</span>{' '}
          <span className="italic">{t('home_tiers_month_2')}</span>
        </h3>
        <p className="text-warm-300 leading-relaxed">
          {t('home_tiers_curated')}
        </p>
      </div>

      <div className="space-y-3">
        {discounts.map((d) => (
          <LocalLink
            key={d.id}
            to={`/locations/${d.slug}`}
            className="group bg-white/5 hover:bg-white/10 rounded-2xl p-4 flex items-center justify-between gap-4 transition-colors"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-10 bg-white/10 rounded-lg shrink-0" />
              <div className="min-w-0">
                <p className="font-heading font-bold text-[10px] tracking-[0.15em] text-warm-300 uppercase">
                  {d.region}
                </p>
                <p className="font-heading font-bold text-brand-primary truncate group-hover:underline">
                  {d.name}
                </p>
              </div>
            </div>
            <PerkTags
              discountPercent={d.discountPercent}
              hasGift={d.hasGift}
              variant="soft"
            />
          </LocalLink>
        ))}
      </div>
    </div>
  </section>
  );
};

export default Tiers;
